import { LDContext } from 'launchdarkly-react-client-sdk';
import type { Auth } from '../context/auth/Auth.types';

/**
 * Builds the LaunchDarkly context for the signed-in user and their selected
 * merchant.
 *
 * The user context keeps its original attributes (including `merchantId`) so
 * existing user-targeted rules keep matching. When a merchant is selected it is
 * also sent as its own `merchant` context, so flags can target the merchant's
 * attributes — notably `brand`, which can differ from the user's brand (e.g. a
 * partner merchant whose users carry the default `fattmerchant` brand).
 */
export function buildLDContext({
  user,
  merchant,
}: Pick<Auth, 'user' | 'merchant'>): LDContext {
  const userAttributes = {
    key: user.id,
    name: user.name,
    email: user.email,
    brand: user.brand,
    merchantId: merchant?.id,
  };

  if (!merchant?.id) {
    return { kind: 'user', ...userAttributes };
  }

  return {
    kind: 'multi',
    user: userAttributes,
    merchant: {
      key: merchant.id,
      name: merchant.company_name,
      brand: merchant.brand,
    },
  };
}
